import { z } from 'zod';

// =============================================================================
// API schemas — single source of truth for user update types
// =============================================================================

export {
  formatNcaaId,
  ncaaIdSchema,
  normalizeNcaaId,
  parseJsonArray,
  parseJsonObjectArray,
  serializeJsonArrayFields,
  type UserUpdateInput,
  userUpdateInputSchema,
} from './schemas';

// =============================================================================
// Common validation schemas shared across apps
// =============================================================================

export const emailSchema = z.string().email();
export const phoneSchema = z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number');
export const urlSchema = z.string().url();
export const cuidSchema = z.string().cuid();

export const paginationSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
});

// =============================================================================
// US-023: Brand profile validators
// =============================================================================

/** Social media handle (without leading @). Allows letters, numbers, dots, underscores, hyphens. */
export const socialHandleSchema = z
  .string()
  .regex(
    /^[a-zA-Z0-9._-]{1,30}$/,
    'Invalid social handle (letters, numbers, dots, underscores, hyphens)',
  )
  .optional();

/**
 * Sport tag for matching. Free-form string with a recommended taxonomy below.
 * Kept loose because some brands (Red Bull, Beats) focus on non-NCAA-standard
 * categories like action_sports, esports, or cycling.
 *
 * Recommended values (use lowercase_snake_case):
 * - NCAA revenue: football, mens_basketball, womens_basketball
 * - NCAA olympic: baseball, softball, volleyball, soccer, track_field,
 *   cross_country, swimming, tennis, golf, gymnastics, wrestling, hockey,
 *   lacrosse, rowing, field_hockey
 * - Non-traditional: action_sports, esports, cycling, surfing, skiing,
 *   snowboarding, skateboarding
 * - Fallback: other
 */
export const sportSchema = z.string().min(1).max(40);

/** Deal types a brand runs or an athlete prefers. */
export const dealTypeSchema = z.enum([
  'endorsement',
  'social_media',
  'appearance',
  'product',
  'event',
  'content_creation',
  'camp_clinic',
  'autograph',
]);

/** Industry / category tags used in both brand profiles and athlete preferences. */
export const industrySchema = z.enum([
  'apparel',
  'footwear',
  'food_beverage',
  'tech',
  'finance',
  'insurance',
  'automotive',
  'beauty',
  'gaming',
  'entertainment',
  'health_wellness',
  'nutrition',
  'telecom',
  'retail',
  'hospitality',
  'education',
  'nonprofit',
  'other',
]);

/** Class year for college athletes. */
export const classYearSchema = z.enum([
  'freshman',
  'sophomore',
  'junior',
  'senior',
  'graduate',
  'redshirt',
]);

/** Division of college athletics. */
export const divisionSchema = z.enum(['d1', 'd2', 'd3', 'naia', 'juco']);

/** Featured athlete entry on a brand profile. */
export const featuredAthleteSchema = z.object({
  name: z.string().min(1).max(100),
  sport: z.string().min(1).max(50),
  school: z.string().min(1).max(100),
});

/** Full Brand profile input (for admin create/update). All expansion fields optional. */
export const brandProfileInputSchema = z.object({
  // Identity
  name: z.string().min(1).max(100),
  logo: urlSchema.optional().nullable(),
  website: urlSchema.optional().nullable(),
  industry: industrySchema.optional().nullable(),
  description: z.string().max(500).optional().nullable(),
  founded: z.number().int().min(1800).max(new Date().getFullYear()).optional().nullable(),
  headquarters: z.string().max(100).optional().nullable(),
  parentCompany: z.string().max(100).optional().nullable(),

  // Profile content
  mission: z.string().max(500).optional().nullable(),
  vision: z.string().max(500).optional().nullable(),
  coreValues: z.array(z.string().min(1).max(50)).max(10).optional().nullable(),

  // NIL program
  nilProgramName: z.string().max(100).optional().nullable(),
  nilLaunchYear: z.number().int().min(2021).max(new Date().getFullYear()).optional().nullable(),
  nilStrategy: z.string().max(500).optional().nullable(),
  sportsFocus: z.array(sportSchema).optional().nullable(),
  dealTypes: z.array(dealTypeSchema).optional().nullable(),
  dealRangeMin: z.number().nonnegative().optional().nullable(),
  dealRangeMax: z.number().nonnegative().optional().nullable(),
  featuredAthletes: z.array(featuredAthleteSchema).max(20).optional().nullable(),
  categories: z.array(z.string().min(1).max(50)).max(15).optional().nullable(),

  // Contact channels (public-facing only)
  prEmail: emailSchema.optional().nullable(),
  partnershipEmail: emailSchema.optional().nullable(),
  instagramHandle: socialHandleSchema.nullable(),
  twitterHandle: socialHandleSchema.nullable(),
  linkedinUrl: urlSchema.optional().nullable(),
  nilContactUrl: urlSchema.optional().nullable(),
  pressPageUrl: urlSchema.optional().nullable(),
});

// =============================================================================
// US-023: Athlete profile expansion validators
// =============================================================================

/**
 * @deprecated Use `userUpdateInputSchema` directly — it is the single source
 * of truth for user update validation. This alias exists only for backward
 * compatibility with any code that imported `athleteProfileUpdateSchema`.
 */
export { userUpdateInputSchema as athleteProfileUpdateSchema } from './schemas';

// =============================================================================
// Form validators (from PR #1 — existing forms in apps/www)
// =============================================================================

// Deal form validation
export const createDealSchema = z.object({
  title: z.string().min(1, 'Deal title is required').max(200),
  brandName: z.string().min(1, 'Brand name is required').max(200),
  amount: z.coerce.number().min(0, 'Amount must be 0 or greater'),
  brandContact: z.string().max(200).optional().or(z.literal('')),
  startDate: z.string().optional().or(z.literal('')),
  endDate: z.string().optional().or(z.literal('')),
  source: z.enum(['dm', 'email', 'agent', 'direct', '']).optional(),
  // Revenue-model origin — drives marketplace-fee accounting. Default
  // self_sourced assumes the athlete found the deal themselves (no fee).
  dealSource: z
    .enum(['self_sourced', 'nilboard_marketplace', 'brand_outbound'])
    .default('self_sourced'),
  description: z.string().max(5000).optional().or(z.literal('')),
  isExclusive: z.boolean(),
});

export type CreateDealInput = z.infer<typeof createDealSchema>;

// Profile update validation — derived from userUpdateInputSchema with
// form-specific overrides for HTML input types (strings for numeric fields).
import { userUpdateInputSchema } from './schemas';

export const updateProfileSchema = userUpdateInputSchema.extend({
  // Form requires name (not optional at form level)
  name: z.string().min(1, 'Name is required').max(200),
  // HTML inputs return strings for numeric fields
  socialFollowers: z.string().optional(),
  socialEngagement: z.string().optional(),
  // Language selector is a constrained enum in the form
  language: z.enum(['en', 'es']),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

// Payment form validation
export const addPaymentSchema = z.object({
  amount: z.coerce.number().positive('Amount must be greater than 0'),
  dueDate: z.string().optional().or(z.literal('')),
  method: z.string().max(100).optional().or(z.literal('')),
  notes: z.string().max(500).optional().or(z.literal('')),
});

export type AddPaymentInput = z.infer<typeof addPaymentSchema>;

// Deliverable form validation
export const addDeliverableSchema = z.object({
  title: z.string().min(1, 'Deliverable title is required').max(200),
  type: z.string().max(100).optional().or(z.literal('')),
  platform: z.string().max(100).optional().or(z.literal('')),
  dueDate: z.string().optional().or(z.literal('')),
});

export type AddDeliverableInput = z.infer<typeof addDeliverableSchema>;

// =============================================================================
// US-028: Payor entity
// =============================================================================

// EIN is 9 digits; we accept formatted (XX-XXXXXXX) or raw and normalize.
export const einSchema = z
  .string()
  .transform((s) => s.replace(/\D/g, ''))
  .refine((s) => s === '' || /^\d{9}$/.test(s), { message: 'EIN must be 9 digits' });

export const payorAffiliationEnum = z.enum(['unknown', 'associated', 'non_associated']);
export type PayorAffiliation = z.infer<typeof payorAffiliationEnum>;

export const payorCreateSchema = z.object({
  legalName: z.string().min(2, 'Legal name is required').max(200),
  displayName: z.string().max(200).optional().or(z.literal('')),
  contactName: z.string().max(200).optional().or(z.literal('')),
  contactEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  contactPhone: z.string().max(40).optional().or(z.literal('')),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  ein: einSchema.optional().or(z.literal('')),
  addressLine1: z.string().max(200).optional().or(z.literal('')),
  addressLine2: z.string().max(200).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  state: z
    .string()
    .regex(/^[A-Z]{2}$/, 'Use 2-letter state code')
    .optional()
    .or(z.literal('')),
  postalCode: z.string().max(20).optional().or(z.literal('')),
  country: z.string().min(2).max(2).default('US'),
  industry: z.string().max(100).optional().or(z.literal('')),
  affiliation: payorAffiliationEnum.default('unknown'),
  organizationId: z.string().optional(),
});

export type PayorCreateInput = z.infer<typeof payorCreateSchema>;

export const payorUpdateSchema = payorCreateSchema.partial().extend({
  id: z.string(),
});

export type PayorUpdateInput = z.infer<typeof payorUpdateSchema>;

// Normalize a typeahead string for matching: lowercase + collapsed whitespace.
export function normalizePayorMatchKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

// =============================================================================
// US-029: Structured Compensation
// =============================================================================

export const compensationTypeEnum = z.enum(['CASH', 'GOODS_SERVICES', 'BONUS', 'ROYALTY']);
export type CompensationType = z.infer<typeof compensationTypeEnum>;

// Shared shape used by create/update on the API and the form. Editing
// nuances (required-when-royalty etc.) are enforced at the boundary via
// .superRefine below.
// Raw object shape. Exported separately so the API's update endpoint can
// call .partial() on it — zod 4 disallows .partial() on schemas that
// already carry a .superRefine() cross-field guard.
export const compensationItemFieldsSchema = z.object({
  type: compensationTypeEnum,
  amount: z.coerce.number().nonnegative('Cash-equivalent must be 0 or greater'),
  currency: z.string().min(1).max(3).default('USD'),
  description: z.string().min(1, 'Add a short description').max(500),
  unitLabel: z.string().max(60).optional(),
  royaltyPercent: z.coerce.number().min(0).max(100).optional(),
  expectedVolumeLow: z.coerce.number().nonnegative().optional(),
  expectedVolumeHigh: z.coerce.number().nonnegative().optional(),
  dueDate: z.string().optional().or(z.literal('')),
  orderIndex: z.number().int().nonnegative().default(0),
});

// Cross-field guards (royalty needs %, volume low ≤ high). Use this on
// CREATE; use compensationItemFieldsSchema.partial() on UPDATE.
export const compensationItemSchema = compensationItemFieldsSchema.superRefine((v, ctx) => {
  if (v.type === 'ROYALTY' && v.royaltyPercent === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['royaltyPercent'],
      message: 'Royalty % is required for royalty items',
    });
  }
  if (
    v.expectedVolumeLow !== undefined &&
    v.expectedVolumeHigh !== undefined &&
    v.expectedVolumeLow > v.expectedVolumeHigh
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['expectedVolumeHigh'],
      message: 'High estimate must be ≥ low estimate',
    });
  }
});

export type CompensationItemInput = z.infer<typeof compensationItemSchema>;

// Friendly defaults for the four types — used by the editor's "Add ..."
// buttons so each row arrives with sensible placeholder content rather
// than a blank line.
export const COMPENSATION_TYPE_DEFAULTS: Record<
  CompensationType,
  { description: string; placeholder: string; helpText?: string }
> = {
  CASH: {
    description: '',
    placeholder: 'e.g., Base payment for Instagram post',
  },
  GOODS_SERVICES: {
    description: '',
    placeholder: 'e.g., 3 pairs of sneakers ($300 retail)',
    helpText:
      'Use the dollar value of what you received. NIL Go counts this toward your $600 reporting threshold.',
  },
  BONUS: {
    description: '',
    placeholder: 'e.g., Bonus if post hits 10K likes',
  },
  ROYALTY: {
    description: '',
    placeholder: 'e.g., 10% of each t-shirt sold',
  },
};

// =============================================================================
// US-030: NIL Go-aligned Activities & Deliverables
// =============================================================================

export const activityCategoryEnum = z.enum([
  'SOCIAL_MEDIA',
  'APPEARANCE',
  'AUTOGRAPH',
  'ENDORSEMENT',
]);
export type ActivityCategory = z.infer<typeof activityCategoryEnum>;

// Per-category metadata schemas. Stored as JSON on Deliverable.metadata so
// new fields can land without a migration; switching categories preserves
// the old payload so an explore-then-revert flow doesn't lose work.
export const socialMediaMetadataSchema = z.object({
  platform: z.enum(['instagram', 'tiktok', 'twitter', 'youtube', 'other']),
  postType: z.enum(['post', 'story', 'reel', 'video', 'other']),
  hashtags: z.array(z.string().max(100)).max(20).default([]),
  mentions: z.array(z.string().max(100)).max(20).default([]),
  expectedReach: z.coerce.number().int().nonnegative().optional(),
});

export const appearanceMetadataSchema = z.object({
  location: z.string().min(1, 'Location is required').max(200),
  datetime: z.string().min(1, 'Date / time is required'),
  durationHours: z.coerce.number().positive('Duration must be greater than 0'),
  isVirtual: z.boolean().default(false),
  audienceSize: z.coerce.number().int().nonnegative().optional(),
});

export const autographMetadataSchema = z.object({
  itemCount: z.coerce.number().int().positive('At least one item is required'),
  perItem: z.boolean().default(false),
  location: z.string().min(1, 'Location is required').max(200),
  datetime: z.string().min(1, 'Date / time is required'),
});

export const endorsementMetadataSchema = z.object({
  medium: z.enum(['print_ad', 'billboard', 'radio', 'tv', 'podcast', 'other']),
  usageRightsWindow: z.string().min(1, 'Usage rights window is required').max(200),
  isExclusiveInCategory: z.boolean().default(false),
});

// Map each category to its metadata schema so callers (router, readiness
// service, UI) can look up the right validator without a switch statement
// at every site.
export const ACTIVITY_METADATA_SCHEMAS = {
  SOCIAL_MEDIA: socialMediaMetadataSchema,
  APPEARANCE: appearanceMetadataSchema,
  AUTOGRAPH: autographMetadataSchema,
  ENDORSEMENT: endorsementMetadataSchema,
} as const satisfies Record<ActivityCategory, z.ZodTypeAny>;

// Base shape used by create + update. .superRefine added below for the
// CREATE path; UPDATE callers should use .partial() on this raw shape (zod
// 4 disallows .partial() on a refined schema — same trick as US-029).
export const deliverableFieldsSchema = z.object({
  title: z.string().min(1, 'Deliverable title is required').max(200),
  description: z.string().max(2000).optional().or(z.literal('')),
  activityCategory: activityCategoryEnum,
  // Freeform legacy hints — kept for back-compat search; the canonical
  // discriminator is `activityCategory` above.
  type: z.string().max(100).optional().or(z.literal('')),
  platform: z.string().max(100).optional().or(z.literal('')),
  dueDate: z.string().optional().or(z.literal('')),
  // Raw object — server stringifies on write, parses on read. Validated
  // category-conditionally in the .superRefine below.
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const deliverableCreateSchema = deliverableFieldsSchema.superRefine((v, ctx) => {
  const schema = ACTIVITY_METADATA_SCHEMAS[v.activityCategory];
  const parsed = schema.safeParse(v.metadata ?? {});
  if (parsed.success) return;
  for (const issue of parsed.error.issues) {
    ctx.addIssue({
      ...issue,
      path: ['metadata', ...issue.path],
    });
  }
});

export type DeliverableCreateInput = z.infer<typeof deliverableCreateSchema>;

export const deliverableProofTypeEnum = z.enum([
  'link',
  'screenshot',
  'metrics_screenshot',
  'document',
]);
export type DeliverableProofType = z.infer<typeof deliverableProofTypeEnum>;

export const deliverableProofSchema = z.object({
  type: deliverableProofTypeEnum,
  url: z.string().url('Paste a valid URL'),
  notes: z.string().max(500).optional().or(z.literal('')),
});

export type DeliverableProofInput = z.infer<typeof deliverableProofSchema>;

// UI metadata: label, emoji, hint text. Keeps the deliverable modal,
// readiness badges, and any future SMS / digest copy reading from one
// source. Emojis match the icons Mike Parrish showed us in NIL Go.
export const ACTIVITY_CATEGORY_DEFAULTS: Record<
  ActivityCategory,
  { label: string; emoji: string; hint: string }
> = {
  SOCIAL_MEDIA: {
    label: 'Social Media',
    emoji: '💬',
    hint: 'Posts, stories, reels — anything on Instagram, TikTok, X, or YouTube.',
  },
  APPEARANCE: {
    label: 'Appearance',
    emoji: '🧍',
    hint: 'In-person or virtual events, meet-and-greets, camp appearances.',
  },
  AUTOGRAPH: {
    label: 'Autograph',
    emoji: '✍️',
    hint: 'Signing sessions, mail-in autograph batches, signed memorabilia.',
  },
  ENDORSEMENT: {
    label: 'Endorsement',
    emoji: '⭐',
    hint: 'Print ads, billboards, radio / TV / podcast spots, brand campaigns.',
  },
};

// Server-side mapper: the legacy `Deliverable.type` freeform string ↦
// NIL Go category. Used by the create handler when callers don't pass an
// `activityCategory` (older clients) and matches the SQL backfill at
// 20260603000000_us_030_activities_deliverables/migration.sql.
export function deriveActivityCategoryFromLegacyType(
  type: string | null | undefined,
): ActivityCategory {
  const t = (type ?? '').trim().toLowerCase();
  if (['post', 'story', 'reel', 'tweet', 'video'].includes(t)) return 'SOCIAL_MEDIA';
  if (['appearance', 'event', 'meet_and_greet'].includes(t)) return 'APPEARANCE';
  if (['autograph', 'signing'].includes(t)) return 'AUTOGRAPH';
  return 'ENDORSEMENT';
}

// =============================================================================
// US-033: NIL Go submission payload
//
// Mirrors NIL Go's submission shape (Deal Terms / Payor / Activities &
// Deliverables / Compensation / Supplemental). Single source of truth for
// the export bundle, the submission preview modal, and the live API
// adapter when it lands. The `submission` row stores this as JSON; we
// re-parse via this schema on every read so a future shape change in
// either direction lights up at the boundary.
// =============================================================================

export const nilGoSubmissionStatusEnum = z.enum([
  'prepared',
  'submitted',
  'under_review',
  'approved',
  'needs_revision',
  'rejected',
  'cancelled',
]);
export type NilGoSubmissionStatus = z.infer<typeof nilGoSubmissionStatusEnum>;

export const nilGoProviderEnum = z.enum(['export_only', 'mock', 'api']);
export type NilGoProvider = z.infer<typeof nilGoProviderEnum>;

export const nilGoPayorPayloadSchema = z.object({
  legalName: z.string(),
  displayName: z.string().nullable(),
  ein: z.string().nullable(),
  affiliation: z.enum(['unknown', 'associated', 'non_associated']),
  address: z.object({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postalCode: z.string().nullable(),
    country: z.string(),
  }),
  industry: z.string().nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  website: z.string().nullable(),
  validationStatus: z.string(),
  validationProvider: z.string().nullable(),
});

export const nilGoActivityPayloadSchema = z.object({
  activityCategory: activityCategoryEnum,
  title: z.string(),
  description: z.string().nullable(),
  dueDate: z.string().nullable(),
  status: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  proofs: z.array(
    z.object({
      type: z.string(),
      url: z.string(),
      capturedAt: z.string(),
    }),
  ),
});

export const nilGoCompensationItemPayloadSchema = z.object({
  type: compensationTypeEnum,
  amount: z.number(),
  currency: z.string(),
  description: z.string(),
  royaltyPercent: z.number().nullable(),
  expectedVolumeLow: z.number().nullable(),
  expectedVolumeHigh: z.number().nullable(),
  unitLabel: z.string().nullable(),
  dueDate: z.string().nullable(),
});

export const nilGoAthletePayloadSchema = z.object({
  name: z.string(),
  email: z.string(),
  ncaaId: z.string(),
  classYear: z.string().nullable(),
  sport: z.string().nullable(),
  school: z.string().nullable(),
  eligibilityVerifiedAt: z.string().nullable(),
  eligibilityVerificationSource: z.string().nullable(),
});

export const nilGoFmvPayloadSchema = z.object({
  estimatedLow: z.number(),
  estimatedMid: z.number(),
  estimatedHigh: z.number(),
  currency: z.string(),
  rangeAssessment: z.enum(['below', 'within', 'above']),
  reasoning: z.string().nullable(),
  generatedAt: z.string(),
});

export const nilGoSubmissionPayloadSchema = z.object({
  // Schema version — bump when payload shape changes so historical
  // snapshots are parseable even after a forward-incompatible edit.
  schemaVersion: z.literal(1),
  submittedAt: z.string(),
  deal: z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    brandName: z.string(),
    currency: z.string(),
    totalCashEquivalent: z.number(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    signedDate: z.string().nullable(),
    isExclusive: z.boolean(),
    exclusivityTerms: z.string().nullable(),
    notes: z.string().nullable(),
    contractUrl: z.string().nullable(),
  }),
  athlete: nilGoAthletePayloadSchema,
  payor: nilGoPayorPayloadSchema,
  activities: z.array(nilGoActivityPayloadSchema),
  compensation: z.array(nilGoCompensationItemPayloadSchema),
  fmv: nilGoFmvPayloadSchema.nullable(),
});

export type NilGoSubmissionPayload = z.infer<typeof nilGoSubmissionPayloadSchema>;

export { formatCurrency, formatDate, formatDateShort, formatNumber } from './format';

// File upload constraints (SSOT shared between /api/upload handler and the
// file-picker UI so allowed types can never drift between the two).
export {
  ALLOWED_UPLOAD_ACCEPT,
  ALLOWED_UPLOAD_EXTENSIONS,
  ALLOWED_UPLOAD_MIME_TYPES,
  type AllowedUploadMimeType,
  isAllowedUploadType,
  MAX_UPLOAD_SIZE_BYTES,
} from './uploads';

export { z };
